import { computed, onMounted, reactive, ref } from 'vue';
import dayjs from 'dayjs';
import { useAlertStore } from '@stores';
import { Calendar } from '@core/dto';
import { RESERVATION_STATUS } from '@core/enums';
import { ERROR_MESSAGES_RESERVATION } from '@constants';

interface State {
  dates: any;
  year: number;
  month: number;
  calendar: Calendar[] | [];
  monthReserveList: Calendar[] | [];
}
export const calendarReadOnly = ref<boolean>(false);
export const useCalendar = (props: any, emit: any) => {
  const alertStore = useAlertStore();

  const state: State = reactive({
    dates: [],
    year: props.year,
    month: props.month,
    calendar: props.calendar,
    monthReserveList: [],
  });

  const isReservable = (day: Calendar) => {
    if (!day.availableStatusByDate) return;
    const status = day.availableStatusByDate as RESERVATION_STATUS;

    return ![
      RESERVATION_STATUS.CLOSE,
      RESERVATION_STATUS.UNOPENED,
      RESERVATION_STATUS.PAST,
      RESERVATION_STATUS.ZERO,
      RESERVATION_STATUS.END,
      RESERVATION_STATUS.WAIT,
    ].includes(status);
  };

  const nowDate = computed(() => dayjs().format('YYYY-MM-DD'));

  const isToday = ({ bsnDate }: Calendar) => bsnDate === nowDate.value;
  const selectDate = async (day: Calendar) => {
    if (calendarReadOnly.value) {
      return;
    }
    if (!day) {
      return;
    }
    if (
      day.availableStatusByDate === RESERVATION_STATUS.END ||
      day.availableStatusByDate === RESERVATION_STATUS.PAST
    ) {
      return await alertStore.showAlertDialog(ERROR_MESSAGES_RESERVATION.END);
    }
    if (day.availableStatusByDate === RESERVATION_STATUS.UNOPENED) {
      return await alertStore.showAlertDialog(
        ERROR_MESSAGES_RESERVATION.UNOPENED
      );
    }
    if (
      (day.reservationAvailableTimeCount || 0) +
        (day.joinReservationAvailableTimeCount || 0) ===
        0 &&
      day.availableStatusByDate !== 'LOTTERY' &&
      day.availableStatusByDate !== 'WAIT'
    ) {
      return await alertStore.showAlertDialog('예약 가능 타임이 없습니다.');
    }

    const selectDay = dayjs(`${state.year}-${state.month}-${day.bsnDe}`).format(
      'YYYY-MM-DD'
    );
    const weekDay = `${dayjs(selectDay).format('YY년MM월DD일')}`;
    emit('selectDay', {
      bsnCode: day.bsnCode,
      selectDay,
      weekDay,
      day,
    });
  };

  const calendarData = () => {
    const [monthFirstDay] = _getFirstDayLastDate(state.year, state.month);
    state.dates = _getMonthOfDays(
      monthFirstDay,
      state.monthReserveList
      // monthLastDate,
      // lastMonthLastDate
    );
  };

  /**
   * 주어진 연도와 월에 대한 첫 번째 날짜의 요일, 해당 월의 마지막 날짜,
   * 그리고 이전 달의 마지막 날짜를 계산하는 함수.
   *
   * @param {number} year - 연도
   * @param {number} month - 월 (1부터 12까지, 1월이 1)
   * @returns {[number, number, number]} 첫 번째 날짜의 요일, 해당 월의 마지막 날짜, 이전 달의 마지막 날짜가 포함된 배열
   */
  const _getFirstDayLastDate = (
    year: number,
    month: number
  ): [number, number, number] => {
    // 주어진 연도와 월의 첫 번째 날짜의 요일을 계산 (0 = 일요일, 1 = 월요일, ...)
    const firstDay = new Date(year, month - 1, 1).getDay();

    // 주어진 연도와 월의 마지막 날짜를 계산
    const lastDate = new Date(year, month, 0).getDate();

    // 이전 달의 연도와 월을 계산
    const prevMonth = month === 1 ? 12 : month - 1;
    const prevYear = month === 1 ? year - 1 : year;

    // 이전 달의 마지막 날짜를 계산
    const prevLastDate = new Date(prevYear, prevMonth, 0).getDate();

    // 첫 번째 날짜의 요일, 해당 월의 마지막 날짜, 이전 달의 마지막 날짜를 반환
    return [firstDay, lastDate, prevLastDate];
  };

  const _getMonthOfDays = (
    monthFirstDay: number,
    monthReserveList: Calendar[]
  ) => {
    let day = 1;
    const dates = [];
    let weekOfDays = [];
    while (day <= monthReserveList.length) {
      if (day === 1) {
        for (let j = 0; j < monthFirstDay; j += 1) {
          weekOfDays.push('');
        }
      }
      weekOfDays.push(monthReserveList[day - 1]);
      if (weekOfDays.length === 7) {
        dates.push(weekOfDays);
        weekOfDays = [];
      }
      day += 1;
    }
    if (weekOfDays.length > 0) dates.push(weekOfDays);
    return dates;
  };

  /**
   * 예약 상태에 따른 텍스트 반환
   */
  const getReservationStatus = (date: Calendar) => {
    if (!date || !date.availableStatusByDate) {
      return '';
    }
    const reservationStatus = date.availableStatusByDate;
    // const joinReservationAvailableTimeCount =
    //   // (date.reservationAvailableTimeCount || 0) +
    //   date.joinReservationAvailableTimeCount || 0;

    switch (reservationStatus) {
      case RESERVATION_STATUS.END:
        return '예약마감';
      case RESERVATION_STATUS.PAST:
        return '예약마감';
      case RESERVATION_STATUS.UNOPENED:
        return '오픈전';
      case RESERVATION_STATUS.CLOSE:
        return '휴장';
      case RESERVATION_STATUS.WAIT:
        return ''; // 대기예약
      case RESERVATION_STATUS.TODAY:
        return '';
      case RESERVATION_STATUS.ING:
        return '';
      // joinReservationAvailableTimeCount
      //   ? `조인${joinReservationAvailableTimeCount}`
      //   : '';
      default:
        return '';
    }
  };

  onMounted(() => {
    state.monthReserveList =
      state.calendar.filter((f) => f.bsnMt === state.month) || [];
    calendarData();
  });

  return {
    state,
    isReservable,
    isToday,
    selectDate,
    getReservationStatus,
    calendarData,
  };
};
